import { Injectable } from "@angular/core";
import { updateQueryStringParameters } from "@obg/global.evo";
import { GtmEventAction } from "@obg/gtm.types/adaptive";
import { isArray } from "lodash-es";

import { LoggingFactory } from "../logging/logging.factory";
import { DocumentRef } from "../platform/document";
import { WindowRef } from "../platform/window";
import { ThemeValueService } from "../utils/theme-value.service";

import { EVENTS_WITH_PRODUCT_THEME } from "./telemetry.const";
import { DEFAULT_EXPERIMENT_VARIANT, VirtualPageView } from "./telemetry.model";
import { TelemetryService } from "./telemetry.service";

export interface ExperimentActivationPayload {
	detail?: {
		expName?: string;
		variation?: string;
	};
}

@Injectable()
export class GtmTelemetryService implements TelemetryService {
	private isGtmInstalled = false;
	private dataLayer: Record<string, any>[] | undefined;

	// A/B experiments
	private experimentResolvePool: Record<string, ((value: string | PromiseLike<string>) => void)[]> = {};
	private experimentVariants: Record<string, string> = {};

	constructor(
		private logging: LoggingFactory,
		private windowRef: WindowRef,
		private documentRef: DocumentRef,
		private themeValueService: ThemeValueService
	) {
		this.dataLayer = (this.windowRef.native as any).dataLayer;
		this.isGtmInstalled = isArray(this.dataLayer);

		if (!this.isGtmInstalled) {
			this.logging.get("gtmTelemetryService").warn("constructor", "Google Tag Manager not detected.");
		}

		this.documentRef.native.addEventListener("ExperimentActivation", this.setVariant.bind(this), false);
	}

	track(event: Record<string, any>): void {
		if (this.isGtmInstalled) {
			let trackedEvent = event;
			const theme = this.themeValueService.getValue();
			if (theme && EVENTS_WITH_PRODUCT_THEME.includes(event.name)) {
				trackedEvent = { ...event, Product_Theme: theme };
			}
			this.dataLayer!.push(trackedEvent);
		}
	}

	trackVirtualPageView(info: VirtualPageView): void {
		const toObfuscate = {
			name: "name",
			username: "user",
			email: "user@email.com",
			token: "00000-00000-0000",
		};
		let trackedInfo = info;
		const theme = this.themeValueService.getValue();
		if (theme) {
			trackedInfo = { ...info, Product_Theme: theme };
		}

		this.track({
			...trackedInfo,
			VirtualUrl: updateQueryStringParameters(info.VirtualUrl, toObfuscate, false),
			event: GtmEventAction.VirtualPageView,
		});
	}

	/**
		* Returns the variant for an experiment
		@remarks
		* getExperimentVariant is called by consumer, which in turn triggers an event ExperimentActivate_x.
		* Google optimize scripts listen for `ExperimentActivate_x` event,
		* therefore triggers ExperimentActivation event, which in turns triggers setVariant wtih relevant data.
		* Since there is timer logic `waitFor` consumer receives the expected variant using the promise.
	*/
	getExperimentVariant(experimentName: string, waitFor = 2000): Promise<string> {
		// Check if variant was already provided (in that case google optimize doesn't trigger custom event again)
		if (this.experimentVariants[experimentName]) {
			return Promise.resolve(this.experimentVariants[experimentName]);
		}

		// We only need to send this event once, even if the variant is not yet received
		if (this.dataLayer?.filter((e) => e.event === `ExperimentActivate_${experimentName}`).length === 0) {
			// First init of A/B experiment
			this.track({
				event: `ExperimentActivate_${experimentName}`,
			});
		}

		return new Promise((resolve) => {
			if (!this.experimentResolvePool[experimentName]) {
				this.experimentResolvePool[experimentName] = [];
			}

			this.experimentResolvePool[experimentName].push(resolve);
			// Timeout if not resolved in time, resolve as original variant
			setTimeout(() => resolve(DEFAULT_EXPERIMENT_VARIANT), waitFor);
		});
	}

	/**
	 * Custom Event handler for setting variant, resolves variant data for related promise
	 */
	private setVariant(payload: ExperimentActivationPayload) {
		if (!payload.detail || !payload.detail.expName || !payload.detail.variation) {
			return;
		}

		this.experimentVariants[payload.detail.expName] = payload.detail.variation;

		if (!this.experimentResolvePool[payload.detail.expName]) {
			return;
		}

		for (const resolveFunc of this.experimentResolvePool[payload.detail.expName]) {
			resolveFunc(this.experimentVariants[payload.detail.expName]);
		}
	}
}
